import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import * as settings from '../api/settings';
import type { SettingsUser } from '../api/settings';

interface CompanyOption {
  id: string;
  displayName: string;
  color: string;
}

interface Props {
  open: boolean;
  users: SettingsUser[]; // for "shares personal with" picker
  companies: CompanyOption[];
  onClose: () => void;
  onDone: (toastMessage: string) => Promise<void> | void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseList(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function randomColor(): string {
  const hues = ['#0ea5c9', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#ec4899', '#22d3ee'];
  return hues[Math.floor(Math.random() * hues.length)];
}

/**
 * Invite a new user (ADR 0009 §6). Wraps `POST /settings/users`:
 * Cognito AdminCreateUser + profile write + own-personal group + optional
 * company memberships + personal-share fan-out. Cognito sends the invite
 * email; the frontend just awaits the sub.
 *
 * Focus + keyboard pattern mirrors NewTaskModal: ESC closes via overlay
 * keydown, click outside closes via overlay onClick, initial focus lands
 * on the email field.
 */
export function InviteUserModal({ open, users, companies, onClose, onDone }: Props) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [handlesText, setHandlesText] = useState('');
  const [addressesText, setAddressesText] = useState('');
  const [color, setColor] = useState('#0ea5c9');
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const [personalShared, setPersonalShared] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setEmail('');
    setDisplayName('');
    setHandlesText('');
    setAddressesText('');
    setColor(randomColor());
    setCompanyIds([]);
    setPersonalShared([]);
    setErr(null);
    setTimeout(() => emailRef.current?.focus(), 0);
  }, [open]);

  const emailValid = useMemo(() => EMAIL_RE.test(email.trim()), [email]);
  const canSubmit = emailValid && displayName.trim().length > 0 && !busy;

  if (!open) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await settings.inviteUser({
        email: email.trim(),
        displayName: displayName.trim(),
        handles: parseList(handlesText),
        knownAddresses: parseList(addressesText).map((a) => a.toLowerCase()),
        color,
        companyIds,
        personalSharedWith: personalShared,
      });
      await onDone(`Invited ${displayName.trim()}`);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function toggleCompany(id: string) {
    setCompanyIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function togglePersonal(sub: string) {
    setPersonalShared((prev) =>
      prev.includes(sub) ? prev.filter((x) => x !== sub) : [...prev, sub]
    );
  }

  return (
    <div
      className="new-task-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Invite user"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <form
        className="new-task-dialog settings-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <header>
          <h3>Invite user</h3>
          <button type="button" onClick={onClose} aria-label="Close">×</button>
        </header>

        <label>
          <span>Email</span>
          <input
            ref={emailRef}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@company.com"
            autoComplete="off"
            required
          />
          {email && !emailValid && (
            <span className="settings-field-hint settings-field-error">
              Enter a valid email.
            </span>
          )}
        </label>

        <label>
          <span>Display name</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Jane Doe"
            required
          />
        </label>

        <div className="new-task-grid">
          <label>
            <span>Handles</span>
            <input
              type="text"
              value={handlesText}
              onChange={(e) => setHandlesText(e.target.value)}
              placeholder="jane, j, jd"
            />
          </label>
          <label>
            <span>Known addresses</span>
            <input
              type="text"
              value={addressesText}
              onChange={(e) => setAddressesText(e.target.value)}
              placeholder="jane@x.com, j@y.com"
            />
          </label>
          <label>
            <span>Color</span>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </label>
        </div>

        {companies.length > 0 && (
          <div className="settings-multiselect">
            <span className="settings-field-label">Companies</span>
            <div className="settings-chip-row">
              {companies.map((c) => {
                const selected = companyIds.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`settings-chip-button ${selected ? 'is-selected' : ''}`}
                    onClick={() => toggleCompany(c.id)}
                  >
                    <span
                      className="settings-color-dot"
                      style={{ backgroundColor: c.color }}
                      aria-hidden="true"
                    />
                    {c.displayName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {users.length > 0 && (
          <div className="settings-multiselect">
            <span className="settings-field-label">Shares personal with</span>
            <div className="settings-chip-row">
              {users.map((u) => {
                const selected = personalShared.includes(u.userId);
                return (
                  <button
                    key={u.userId}
                    type="button"
                    className={`settings-chip-button ${selected ? 'is-selected' : ''}`}
                    onClick={() => togglePersonal(u.userId)}
                  >
                    <span
                      className="settings-color-dot"
                      style={{ backgroundColor: u.color }}
                      aria-hidden="true"
                    />
                    {u.displayName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {err && <div className="settings-error">{err}</div>}

        <div className="new-task-footer">
          <span className="muted small">
            The user will receive an email invitation with a temporary password.
          </span>
          <button type="submit" className="primary" disabled={!canSubmit}>
            {busy ? 'Inviting…' : 'Send invite'}
          </button>
        </div>
      </form>
    </div>
  );
}
